import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useDoubleTap } from "use-double-tap";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { setActiveElements } from "../../../../../redux/slices/controllerSlice";
import { conditions, handleRippleCircle, videoTag } from "../../../../../helpers";
import { handleBackwardForward } from "../../../../../redux/actions/controller.actions";
import { ConditionName, IBackwardForwardProps } from "../../../../../interfaces";
import IMAGES from "../../../../../images/importImages";
import { shallowEqual } from "react-redux";

const hideTime = 1000;

const BackwardForward = ({
    type,
    isRightDoubleTouched,
    isLeftDoubleTouched,
    setIsRightDoubleTouched,
    setIsLeftDoubleTouched
}: IBackwardForwardProps) => {
    const [backwardForwardCount, setBackwardForwardCount] = useState<number>(15);
    const [hide, setHide] = useState<boolean>(false);
    const backwardForwardCountRef = useRef<number>(backwardForwardCount);

    const dispatch = useAppDispatch();
    const { duration } = useAppSelector(({ controller: { duration, showMiniPlayer } }) => ({ duration, showMiniPlayer }), shallowEqual);
    const { videoElementSizeInfo } = useAppSelector(
        ({ videoData: { videoElementSizeInfo, params } }) => ({ videoElementSizeInfo, params }),
        shallowEqual
    );

    const timeoutRef = useRef<any>(null);

    useEffect(() => {
        backwardForwardCountRef.current = backwardForwardCount;
    }, [backwardForwardCount]);

    let doubleTouch: any = undefined;

    doubleTouch = useDoubleTap((event) => {
        if (["touchBackward", "touchForward"].includes(type)) {
            event.stopPropagation();
            if (type === "touchBackward" && setIsLeftDoubleTouched) setIsLeftDoubleTouched(true);
            else if (type === "touchForward" && setIsRightDoubleTouched) setIsRightDoubleTouched(true);
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => setHide(true), hideTime);
        }
    }, 200);

    useEffect(() => {
        if (hide) {
            setHide(false);
            dispatch(setActiveElements(false));
            if (isRightDoubleTouched && setIsRightDoubleTouched) {
                setIsRightDoubleTouched(false);
                if (videoTag().duration - videoTag().currentTime <= backwardForwardCountRef.current) handleBackwardForward(type, videoTag().duration);
                else handleBackwardForward(type, backwardForwardCountRef.current);
                setBackwardForwardCount(15);
            } else if (isLeftDoubleTouched && setIsLeftDoubleTouched) {
                setIsLeftDoubleTouched(false);
                if (videoTag().currentTime <= backwardForwardCountRef.current) handleBackwardForward(type, 0);
                else handleBackwardForward(type, backwardForwardCountRef.current);
                setBackwardForwardCount(15);
            }
        }
    }, [hide]);

    const handleTouchStart = (event: any) => {
        setBackwardForwardCount((prev) => prev + 15);
        if (conditions(ConditionName.isTouchScreenLandscape)) handleRippleCircle(event, type);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setHide(true), hideTime);
    };

    let component;

    if (["backward", "forward"].includes(type)) {
        component = (
            <div
                className={`${
                    !conditions(ConditionName.isTouchScreen) && "hover:pl-scale-125"
                } pl-transition-transform pl-ease-in-out pl-cursor-pointer ${
                    type === "backward" && (videoTag().currentTime >= 15 ? "pl-opacity-100" : "pl-opacity-30 pl-cursor-default hover:pl-scale-100")
                } ${
                    type === "forward" &&
                    (duration - videoTag().currentTime >= 15 ? "pl-opacity-100" : "pl-opacity-30 pl-cursor-default hover:pl-scale-100")
                }}`}
                onContextMenu={(event) => event.preventDefault()}
            >
                <Image
                    alt={type === "backward" ? "backward-icon" : "forward-icon"}
                    src={type === "backward" ? IMAGES.backward15 : IMAGES.forward15}
                    width={conditions(ConditionName.isMiniPlayer) ? 30 : conditions(ConditionName.isTouchScreen) ? 32 : 48}
                    height={conditions(ConditionName.isMiniPlayer) ? 30 : conditions(ConditionName.isTouchScreen) ? 32 : 48}
                    onClick={() => handleBackwardForward(type, backwardForwardCount)}
                    onDoubleClick={(event: any) => event.stopPropagation()}
                />
            </div>
        );
    } else if (["touchBackward", "touchForward"].includes(type)) {
        component = (
            <div
                id={type}
                className={`pl-relative pl-flex pl-flex-col pl-justify-center pl-items-center pl-gap-2 pl-overflow-hidden ${
                    type === "touchBackward" ? "pl-rounded-r-full" : "pl-rounded-l-full"
                }`}
                style={{ width: videoElementSizeInfo.width, height: videoElementSizeInfo.width }}
                {...doubleTouch}
            >
                {(isRightDoubleTouched || isLeftDoubleTouched) && (
                    <div
                        className={`pl-w-full pl-h-full pl-flex pl-flex-col pl-justify-center pl-items-center pl-gap-2 pl-p-5 pl-bg-secondary-100/[0.3]`}
                        onTouchStart={handleTouchStart}
                    >
                        <div
                            className={`${
                                type === "touchBackward" ? "pl-arrow-left" : "pl-arrow-right pl-gap-2"
                            } pl-flex pl-justify-center pl-items-center`}
                        >
                            <Image
                                alt="arrow-left-icon"
                                src={IMAGES.arrowLeft}
                                className={`${type === "touchForward" && "pl-rotate-180"}`}
                                width={24}
                                height={24}
                            />
                            <Image
                                alt="arrow-left-icon"
                                src={IMAGES.arrowLeft}
                                className={`${type === "touchForward" && "pl-rotate-180"}`}
                                width={24}
                                height={24}
                            />
                            <Image
                                alt="arrow-left-icon"
                                src={IMAGES.arrowLeft}
                                className={`${type === "touchForward" && "pl-rotate-180"}`}
                                width={24}
                                height={24}
                            />
                        </div>
                        <p className="pl-text-center pl-text-[14px] pl-font-normal pl-text-white">{backwardForwardCount} ثانیه</p>
                    </div>
                )}
            </div>
        );
    }

    return <>{component}</>;
};

export default BackwardForward;
